import {isCopilot} from '@github-ui/assignees/copilot-user'
import {CreateIssueDialogEntry} from '@github-ui/issue-create/CreateIssueDialogEntry'
import {assigneesSectionAssignees, assigneesSectionFragment} from '@github-ui/issue-metadata/AssigneesSection'
import type {AssigneesSectionAssignees$key} from '@github-ui/issue-metadata/AssigneesSectionAssignees.graphql'
import type {AssigneesSectionFragment$key} from '@github-ui/issue-metadata/AssigneesSectionFragment.graphql'
import {fieldsSectionFieldValuesFragment, fieldsSectionFragment} from '@github-ui/issue-metadata/FieldsSection'
import type {FieldsSectionFieldValues$key} from '@github-ui/issue-metadata/FieldsSectionFieldValues.graphql'
import type {FieldsSectionFragment$key} from '@github-ui/issue-metadata/FieldsSectionFragment.graphql'
import {parseIssueFieldValues} from '@github-ui/issue-metadata/IssueFieldWithUnsavedValue'
import {LabelsSectionAssignedLabelsFragment, labelsSectionFragment} from '@github-ui/issue-metadata/LabelsSection'
import type {LabelsSectionAssignedLabels$key} from '@github-ui/issue-metadata/LabelsSectionAssignedLabels.graphql'
import type {LabelsSectionFragment$key} from '@github-ui/issue-metadata/LabelsSectionFragment.graphql'
import {MilestonesSectionFragment, milestonesSectionMilestone} from '@github-ui/issue-metadata/MilestonesSection'
import type {MilestonesSectionFragment$key} from '@github-ui/issue-metadata/MilestonesSectionFragment.graphql'
import type {MilestonesSectionMilestone$key} from '@github-ui/issue-metadata/MilestonesSectionMilestone.graphql'
import {projectSectionFragment} from '@github-ui/issue-metadata/ProjectsSection'
import type {ProjectsSectionFragment$key} from '@github-ui/issue-metadata/ProjectsSectionFragment.graphql'
import {TypesSectionFragment, TypesSectionTypeFragment} from '@github-ui/issue-metadata/TypesSection'
import type {TypesSectionFragment$key} from '@github-ui/issue-metadata/TypesSectionFragment.graphql'
import type {TypesSectionTypeFragment$key} from '@github-ui/issue-metadata/TypesSectionTypeFragment.graphql'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import {useNavigate} from '@github-ui/use-navigate'
import {useMemo} from 'react'
import {useFragment} from 'react-relay'

import type {IssueSidebarPrimaryQuery$data} from './__generated__/IssueSidebarPrimaryQuery.graphql'
import type {OptionConfig} from './OptionConfig'

export type DuplicateIssueDialogProps = {
  showDuplicateIssueDialog: boolean
  setShowDuplicateIssueDialog: (value: boolean) => void
  optionConfig: OptionConfig
  repositoryOwner: string
  repositoryName: string
  title: string
  body: string
  issue: IssueSidebarPrimaryQuery$data
}

export const DuplicateIssueDialog = ({
  title,
  body,
  showDuplicateIssueDialog,
  setShowDuplicateIssueDialog,
  optionConfig,
  repositoryOwner,
  repositoryName,
  issue,
}: DuplicateIssueDialogProps) => {
  const navigate = useNavigate()
  const assigneesSection = useFragment<AssigneesSectionFragment$key>(assigneesSectionFragment, issue)
  const assigneesData = useFragment<AssigneesSectionAssignees$key>(assigneesSectionAssignees, assigneesSection)

  const typeSection = useFragment<TypesSectionFragment$key>(TypesSectionFragment, issue)
  const typeData = useFragment<TypesSectionTypeFragment$key>(TypesSectionTypeFragment, typeSection)

  const labelsSection = useFragment<LabelsSectionFragment$key>(labelsSectionFragment, issue)
  const labelsData = useFragment<LabelsSectionAssignedLabels$key>(LabelsSectionAssignedLabelsFragment, labelsSection)

  const milestoneSection = useFragment<MilestonesSectionFragment$key>(MilestonesSectionFragment, issue)
  const milestoneData = useFragment<MilestonesSectionMilestone$key>(milestonesSectionMilestone, milestoneSection)

  const projectsData = useFragment<ProjectsSectionFragment$key>(projectSectionFragment, issue)

  const fieldsSection = useFragment<FieldsSectionFragment$key>(fieldsSectionFragment, issue)
  const fieldsData = useFragment<FieldsSectionFieldValues$key>(fieldsSectionFieldValuesFragment, fieldsSection)

  const assignees = useMemo(() => {
    return (assigneesData.assignedActors?.nodes || [])
      .flatMap(assignee => (assignee ? [assignee] : []))
      .filter(assignee => assignee.login && !isCopilot(assignee.login)) as AssigneePickerAssignee[]
  }, [assigneesData])

  const issueType = useMemo(() => {
    return typeData.issueType || undefined
  }, [typeData])

  const labels = useMemo(() => {
    return (labelsData.labels?.edges || []).flatMap(e => (e?.node ? [e?.node] : []))
  }, [labelsData])

  const milestone = milestoneData.milestone ? milestoneData.milestone : undefined

  const projects = useMemo(() => {
    const projectItems = (projectsData.projectItems?.edges ?? [])
      .flatMap(edge => (edge?.node ? edge?.node : []))
      .filter(projectItem => projectItem.project.closed !== true)

    return projectItems.map(projectItem => projectItem.project)
  }, [projectsData])

  const issueFields = useMemo(() => parseIssueFieldValues(fieldsData.issueFieldValues?.nodes ?? []), [fieldsData])

  return (
    <CreateIssueDialogEntry
      navigate={url => navigate(url, {reloadDocument: true})}
      isCreateDialogOpen={showDuplicateIssueDialog}
      setIsCreateDialogOpen={setShowDuplicateIssueDialog}
      optionConfig={{
        ...optionConfig,
        showRepositoryPicker: true,
        defaultDisplayMode: 'IssueDuplication',
        issueCreateArguments: {
          repository: {
            owner: repositoryOwner,
            name: repositoryName,
          },
          initialValues: {
            title,
            body,
            assignees,
            type: issueType,
            labels,
            milestone,
            projects,
            issueFields,
          },
        },
      }}
    />
  )
}
